import { useMemo } from 'react'
import { Badge, Box, Button, Group, Paper, Stack, Text, Tooltip } from '@mantine/core'
import { formatDateTimeDot } from '@/lib/ticket-display'
import type { TicketTimelineItem, TicketTimelineMetrics } from '@/types/api'

function formatTimelineDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds)) return 'Начало'
  const s = Math.max(0, Math.floor(seconds))
  if (s === 0) return '0'
  const days = Math.floor(s / 86400)
  const hours = Math.floor((s % 86400) / 3600)
  const minutes = Math.floor((s % 3600) / 60)
  const restSeconds = s % 60
  const parts: string[] = []
  if (days) parts.push(`${days} д`)
  if (hours) parts.push(`${hours} ч`)
  if (minutes) parts.push(`${minutes} мин`)
  if (restSeconds || parts.length === 0) parts.push(`${restSeconds} сек`)
  return parts.slice(0, days ? 3 : 4).join(' ')
}

function formatTimelineMetricDuration(seconds: number | null | undefined): string {
  if (seconds == null || !Number.isFinite(seconds)) return '—'
  return formatTimelineDuration(seconds)
}

function timelineCategoryColor(category: string): string {
  if (category === 'created') return 'blue'
  if (category === 'assignment') return 'violet'
  if (category === 'redistribution') return 'pink'
  if (category === 'status') return 'green'
  if (category === 'priority') return 'orange'
  if (category === 'message_customer') return 'cyan'
  if (category === 'message_operator') return 'indigo'
  if (category === 'message_ai') return 'grape'
  if (category === 'help') return 'red'
  if (category === 'review') return 'teal'
  if (category === 'tag') return 'gray'
  return 'gray'
}

export function TicketTimelineView({
  items,
  fromId,
  toId,
  selectedIds,
  onPick,
}: {
  items: TicketTimelineItem[]
  fromId?: string | null
  toId?: string | null
  selectedIds?: string[]
  onPick?: (item: TicketTimelineItem) => void
}) {
  if (!items.length) {
    return (
      <Box py="xl">
        <Text size="sm" c="dimmed" ta="center">
          По обращению пока нет событий.
        </Text>
      </Box>
    )
  }
  const fromIndex = fromId ? items.findIndex((item) => item.id === fromId) : -1
  const toIndex = toId ? items.findIndex((item) => item.id === toId) : -1
  const hasSelection = fromIndex >= 0 || toIndex >= 0
  const rangeStart =
    fromIndex >= 0 && toIndex >= 0 ? Math.min(fromIndex, toIndex) : fromIndex >= 0 ? fromIndex : toIndex
  const rangeEnd = fromIndex >= 0 && toIndex >= 0 ? Math.max(fromIndex, toIndex) : fromIndex >= 0 ? fromIndex : toIndex
  const selectedIdSet = new Set(selectedIds ?? [])
  const hasSelectedSet = selectedIdSet.size > 0

  return (
    <Stack gap={0}>
      {items.map((item, index) => {
        const color = timelineCategoryColor(item.category)
        const createdAt = item.created_at ? formatDateTimeDot(item.created_at) : '—'
        const actor = item.actor_name || item.actor_email || 'Система'
        const titleIncludesActor = Boolean(actor && item.title.toLowerCase().includes(actor.toLowerCase()))
        const summaryIncludesActor = Boolean(
          actor && typeof item.summary === 'string' && item.summary.toLowerCase().includes(actor.toLowerCase()),
        )
        const showActorMeta =
          item.category === 'created' ||
          item.category === 'note' ||
          item.category === 'system' ||
          (!titleIncludesActor && !summaryIncludesActor && actor !== 'Система')
        const isFrom = item.id === fromId
        const isTo = item.id === toId
        const isSelected = selectedIdSet.has(item.id)
        const isInSelectedRange =
          !hasSelectedSet &&
          hasSelection &&
          rangeStart >= 0 &&
          rangeEnd >= 0 &&
          index >= rangeStart &&
          index <= rangeEnd
        const isDimmed = hasSelectedSet ? !isSelected : hasSelection && !isInSelectedRange
        const rowBackground = isSelected
          ? 'color-mix(in srgb, var(--mantine-color-yellow-1), transparent 55%)'
          : isFrom
            ? 'color-mix(in srgb, var(--mantine-color-blue-1), transparent 45%)'
            : isTo
              ? 'color-mix(in srgb, var(--mantine-color-green-1), transparent 45%)'
              : isInSelectedRange && fromIndex >= 0 && toIndex >= 0
                ? 'color-mix(in srgb, var(--mantine-color-yellow-1), transparent 70%)'
                : undefined
        const canPick = Boolean(onPick && item.created_at)
        return (
          <Box
            key={item.id}
            role={canPick ? 'button' : undefined}
            tabIndex={canPick ? 0 : undefined}
            onClick={canPick ? () => onPick?.(item) : undefined}
            onKeyDown={
              canPick
                ? (event) => {
                    if (event.key === 'Enter' || event.key === ' ') {
                      event.preventDefault()
                      onPick?.(item)
                    }
                  }
                : undefined
            }
            style={{
              display: 'grid',
              gridTemplateColumns: '96px 22px minmax(0, 1fr)',
              columnGap: 10,
              alignItems: 'stretch',
              cursor: canPick ? 'pointer' : undefined,
              opacity: isDimmed ? 0.36 : 1,
              filter: isDimmed ? 'grayscale(0.35)' : undefined,
              background: rowBackground,
              borderRadius: isSelected || isFrom || isTo || isInSelectedRange ? 6 : undefined,
              transition: 'opacity 120ms ease, filter 120ms ease, background 120ms ease',
            }}
          >
            <Box pt={index === 0 ? 2 : 14} style={{ textAlign: 'right' }}>
              <Text size="xs" c={index === 0 ? 'dimmed' : 'blue'} fw={index === 0 ? 400 : 600}>
                {formatTimelineDuration(item.delta_seconds)}
              </Text>
            </Box>
            <Box
              style={{
                position: 'relative',
                display: 'flex',
                justifyContent: 'center',
              }}
            >
              <Box
                style={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  width: 1,
                  background: 'var(--app-shell-border-color)',
                }}
              />
              <Box
                mt={index === 0 ? 4 : 16}
                style={{
                  width: isFrom || isTo ? 16 : 11,
                  height: isFrom || isTo ? 16 : 11,
                  borderRadius: 999,
                  background: isFrom
                    ? 'var(--mantine-color-blue-6)'
                    : isTo
                      ? 'var(--mantine-color-green-6)'
                      : isSelected
                        ? 'var(--mantine-color-yellow-6)'
                        : isInSelectedRange && fromIndex >= 0 && toIndex >= 0
                          ? 'var(--mantine-color-yellow-6)'
                          : `var(--mantine-color-${color}-6)`,
                  border: '2px solid var(--mantine-color-body)',
                  position: 'relative',
                  zIndex: 1,
                }}
              />
            </Box>
            <Box
              pb="sm"
              pt={index === 0 ? 0 : 10}
              style={{
                minWidth: 0,
                borderBottom: index === items.length - 1 ? undefined : '1px solid var(--app-shell-border-color)',
              }}
            >
              <Group gap={6} wrap="nowrap" align="center" style={{ minWidth: 0 }}>
                {isFrom ? (
                  <Badge size="xs" color="blue" variant="filled" style={{ flexShrink: 0 }}>
                    от
                  </Badge>
                ) : null}
                {isTo ? (
                  <Badge size="xs" color="green" variant="filled" style={{ flexShrink: 0 }}>
                    до
                  </Badge>
                ) : null}
                <Badge size="xs" color={color} variant="light" style={{ flexShrink: 0 }}>
                  {item.title}
                </Badge>
                <Text size="xs" c="dimmed" truncate>
                  {createdAt}
                </Text>
              </Group>
              {item.summary ? (
                <Text size="sm" mt={4} style={{ whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
                  {item.summary}
                </Text>
              ) : null}
              {item.description && item.description !== item.summary ? (
                <Text size="xs" c="dimmed" mt={3} style={{ overflowWrap: 'anywhere' }}>
                  {item.description}
                </Text>
              ) : null}
              {showActorMeta ? (
                <Group gap={6} mt={5} wrap="wrap">
                  <Text size="xs" c="dimmed">
                    {actor}
                  </Text>
                  {item.actor_email && item.actor_email !== item.actor_name ? (
                    <Text size="xs" c="dimmed" style={{ overflowWrap: 'anywhere' }}>
                      {item.actor_email}
                    </Text>
                  ) : null}
                </Group>
              ) : null}
            </Box>
          </Box>
        )
      })}
    </Stack>
  )
}

export function TicketTimelineMetricsPanel({ metrics }: { metrics?: TicketTimelineMetrics | null }) {
  const items = [
    {
      label: 'Обращение',
      description: 'Создание → первое назначение',
      value: formatTimelineMetricDuration(metrics?.created_to_first_assignment_seconds),
      hint: 'Время от создания обращения до первого назначения',
    },
    {
      label: 'Бот',
      description: 'Назначение → первый ответ',
      value: formatTimelineMetricDuration(metrics?.bot_assignment_to_first_reply_seconds),
      hint: 'Время первого ответа бота минус время назначения на бота',
    },
    {
      label: 'Бот',
      description: 'Первый ответ → назначение оператора',
      value: formatTimelineMetricDuration(metrics?.first_bot_reply_to_first_operator_assignment_seconds),
      hint: 'Время первого назначения на оператора минус время первого ответа бота',
    },
    {
      label: 'Оператор',
      description: 'Назначение → первый ответ',
      value: formatTimelineMetricDuration(metrics?.assignment_to_first_operator_reply_seconds),
      hint: 'Время первого ответа оператора минус время первого назначения на оператора',
    },
    {
      label: 'Оператор',
      description: `Ср. скорость ответа клиенту; Замеров: ${metrics?.operator_reply_samples ?? 0}`,
      value: formatTimelineMetricDuration(metrics?.average_operator_reply_after_customer_seconds),
      hint: `Среднее время ответа оператора на сообщения клиента. Замеров: ${metrics?.operator_reply_samples ?? 0}`,
    },
    {
      label: 'Оператор',
      description: 'Назначение → последний ответ',
      value: formatTimelineMetricDuration(metrics?.first_operator_assignment_to_last_reply_seconds),
      hint: 'Время последнего ответа оператора минус время первого назначения на оператора',
    },
    {
      label: 'Переназначений',
      description: `Смен назначений: ${metrics?.assignment_changes_count ?? 0}`,
      value: String(metrics?.reassignment_count ?? 0),
      hint: `всего смен назначений: ${metrics?.assignment_changes_count ?? 0}`,
    },
    {
      label: 'Перераспределений',
      description: `Активно сейчас: ${metrics?.active_redistribution_count ?? 0}`,
      value: String(metrics?.redistribution_count ?? 0),
      hint: `активно сейчас: ${metrics?.active_redistribution_count ?? 0}`,
    },
  ]

  return (
    <Box
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(210px, 1fr))',
        gap: 8,
      }}
    >
      {items.map((item) => (
        <Tooltip
          key={`${item.label}:${item.description}`}
          label={`${item.label}. ${item.description}. ${item.hint}`}
          multiline
          w={280}
        >
          <Paper withBorder radius="sm" p="xs" style={{ minWidth: 0, minHeight: 112 }}>
            <Text
              size="xs"
              c="dimmed"
              fw={700}
              tt="uppercase"
              style={{ whiteSpace: 'normal', overflowWrap: 'anywhere' }}
            >
              {item.label}
            </Text>
            <Text size="lg" fw={700} lh={1.2} mt={2}>
              {item.value}
            </Text>
            <Text
              size="xs"
              c="dimmed"
              mt={2}
              style={{ whiteSpace: 'normal', overflowWrap: 'anywhere', lineHeight: 1.25 }}
            >
              {item.description}
            </Text>
          </Paper>
        </Tooltip>
      ))}
    </Box>
  )
}

function timelineEventSelectLabel(item: TicketTimelineItem, index: number): string {
  const time = item.created_at ? formatDateTimeDot(item.created_at) : '—'
  const base = `${index + 1}. ${time} · ${item.title}`
  return base.length > 90 ? `${base.slice(0, 87).trim()}...` : base
}

export type TimelineQuickRangeKey = 'assignment' | 'unassigned' | 'redistribution' | 'status' | 'operator' | 'customer'

export function TicketTimelineAnalysisPanel({
  items,
  metrics,
  fromId,
  toId,
  selectedIds,
  selectedKind,
  onSelectItems,
  onResetRange,
}: {
  items: TicketTimelineItem[]
  metrics?: TicketTimelineMetrics | null
  fromId?: string | null
  toId?: string | null
  selectedIds?: string[]
  selectedKind?: TimelineQuickRangeKey | null
  onSelectItems: (ids: string[], kind: TimelineQuickRangeKey) => void
  onResetRange: () => void
}) {
  const selectableItems = useMemo(
    () => items.filter((item) => item.created_at).map((item, index) => ({ item, index })),
    [items],
  )
  const fromItem = selectableItems.find(({ item }) => item.id === fromId)?.item ?? null
  const toItem = selectableItems.find(({ item }) => item.id === toId)?.item ?? null
  const intervalSeconds = useMemo(() => {
    if (!fromItem?.created_at || !toItem?.created_at) return null
    const from = new Date(fromItem.created_at).getTime()
    const to = new Date(toItem.created_at).getTime()
    if (!Number.isFinite(from) || !Number.isFinite(to)) return null
    return Math.max(0, Math.floor((to - from) / 1000))
  }, [fromItem?.created_at, toItem?.created_at])
  const selectedIdSet = useMemo(() => new Set(selectedIds ?? []), [selectedIds])
  const selectedItems = useMemo(
    () => selectableItems.filter(({ item }) => selectedIdSet.has(item.id)),
    [selectableItems, selectedIdSet],
  )
  const selectedStats = useMemo(() => {
    const toMs = (value?: string | null) => {
      if (!value) return null
      const ms = new Date(value).getTime()
      return Number.isFinite(ms) ? ms : null
    }
    if (selectedKind === 'operator') {
      let pendingCustomerMs: number | null = null
      let total = 0
      let samples = 0
      for (const { item } of selectableItems) {
        const itemMs = toMs(item.created_at)
        if (itemMs == null) continue
        if (item.category === 'message_customer') {
          pendingCustomerMs = itemMs
          continue
        }
        if (item.category === 'message_operator') {
          if (selectedIdSet.has(item.id) && pendingCustomerMs != null) {
            total += Math.max(0, Math.floor((itemMs - pendingCustomerMs) / 1000))
            samples += 1
          }
          pendingCustomerMs = null
        }
      }
      return {
        label: 'Ожидание оператора',
        hint: 'от сообщения клиента до ответа оператора',
        totalSeconds: total,
        samples,
      }
    }
    if (selectedKind === 'customer') {
      let pendingSupportMs: number | null = null
      let total = 0
      let samples = 0
      for (const { item } of selectableItems) {
        const itemMs = toMs(item.created_at)
        if (itemMs == null) continue
        if (item.category === 'message_operator' || item.category === 'message_ai') {
          pendingSupportMs = itemMs
          continue
        }
        if (item.category === 'message_customer') {
          if (selectedIdSet.has(item.id) && pendingSupportMs != null) {
            total += Math.max(0, Math.floor((itemMs - pendingSupportMs) / 1000))
            samples += 1
          }
          pendingSupportMs = null
        }
      }
      return {
        label: 'Ожидание клиента',
        hint: 'от ответа оператора/бота до сообщения клиента',
        totalSeconds: total,
        samples,
      }
    }
    const totalSeconds = selectedItems.reduce((sum, { item }) => sum + Math.max(0, Number(item.delta_seconds || 0)), 0)
    return {
      label: 'Суммарно',
      hint: 'сумма времени перед выбранными событиями',
      totalSeconds,
      samples: selectedItems.length,
    }
  }, [selectableItems, selectedIdSet, selectedItems, selectedKind])
  const redistributionRows = metrics?.redistribution_by_operator ?? []
  const quickRanges = useMemo(() => {
    const configs: Array<{
      key: TimelineQuickRangeKey
      label: string
      color: string
      test: (item: TicketTimelineItem) => boolean
    }> = [
      { key: 'assignment', label: 'Назначения', color: 'violet', test: (item) => item.category === 'assignment' },
      {
        key: 'unassigned',
        label: 'Снятие назначения',
        color: 'gray',
        test: (item) => item.activity_type === 'ticket_unassigned',
      },
      {
        key: 'redistribution',
        label: 'Перераспределение',
        color: 'pink',
        test: (item) => item.category === 'redistribution',
      },
      { key: 'status', label: 'Статусы', color: 'green', test: (item) => item.category === 'status' },
      {
        key: 'operator',
        label: 'Ответы оператора',
        color: 'indigo',
        test: (item) => item.category === 'message_operator',
      },
      {
        key: 'customer',
        label: 'Сообщения клиента',
        color: 'cyan',
        test: (item) => item.category === 'message_customer',
      },
    ]
    return configs
      .map((config) => {
        const matched = selectableItems.filter(({ item }) => config.test(item))
        return { ...config, matched }
      })
      .filter((config) => config.matched.length > 0)
  }, [selectableItems])

  return (
    <Paper withBorder radius="sm" p="sm">
      <Stack gap="sm">
        <Group align="stretch" wrap="wrap">
          <Paper withBorder radius="sm" p="xs" style={{ flex: '1 1 220px', minWidth: 0 }}>
            <Text size="xs" c="dimmed">
              {selectedItems.length ? 'Выбрано' : 'От'}
            </Text>
            <Text size="sm" fw={600} truncate>
              {selectedItems.length
                ? `${selectedItems.length} событий`
                : fromItem
                  ? timelineEventSelectLabel(
                      fromItem,
                      selectableItems.find(({ item }) => item.id === fromItem.id)?.index ?? 0,
                    )
                  : 'Не выбрано'}
            </Text>
          </Paper>
          <Paper withBorder radius="sm" p="xs" style={{ flex: '1 1 220px', minWidth: 0 }}>
            <Text size="xs" c="dimmed">
              {selectedItems.length ? 'Что считается' : 'До'}
            </Text>
            <Text size="sm" fw={600} truncate>
              {selectedItems.length
                ? selectedStats.hint
                : toItem
                  ? timelineEventSelectLabel(
                      toItem,
                      selectableItems.find(({ item }) => item.id === toItem.id)?.index ?? 0,
                    )
                  : 'Не выбрано'}
            </Text>
          </Paper>
          <Paper withBorder radius="sm" p="xs" style={{ minWidth: 150 }}>
            <Text size="xs" c="dimmed">
              {selectedItems.length ? selectedStats.label : 'Интервал'}
            </Text>
            <Text fw={700} size="lg" lh={1.2}>
              {formatTimelineMetricDuration(selectedItems.length ? selectedStats.totalSeconds : intervalSeconds)}
            </Text>
            {selectedItems.length && selectedStats.samples !== selectedItems.length ? (
              <Text size="xs" c="dimmed" mt={2}>
                {selectedStats.samples} замеров
              </Text>
            ) : null}
          </Paper>
          <Button
            variant="light"
            color="gray"
            onClick={onResetRange}
            disabled={!fromId && !toId && !selectedItems.length}
          >
            Сбросить
          </Button>
        </Group>
        {quickRanges.length ? (
          <Group gap={4} wrap="wrap">
            {quickRanges.map((range) => {
              const ids = range.matched.map(({ item }) => item.id)
              return (
                <Badge
                  key={range.key}
                  component="button"
                  type="button"
                  color={range.color}
                  variant="light"
                  size="sm"
                  onClick={() => onSelectItems(ids, range.key)}
                  style={{ cursor: 'pointer', textTransform: 'none', fontWeight: 500 }}
                >
                  {range.label}: {range.matched.length}
                </Badge>
              )
            })}
          </Group>
        ) : null}
        {redistributionRows.length ? (
          <Box>
            <Text size="xs" c="dimmed" mb={6}>
              Перераспределение по операторам
            </Text>
            <Stack gap={4}>
              {redistributionRows.map((row) => (
                <Group
                  key={row.operator_id || row.operator_name || 'unknown'}
                  gap={6}
                  wrap="nowrap"
                  style={{ minWidth: 0 }}
                >
                  <Badge color="pink" variant="light" size="xs" style={{ flexShrink: 0 }}>
                    {row.count} раз{row.active_count ? `, активно ${row.active_count}` : ''}
                  </Badge>
                  <Text size="xs" fw={500} truncate style={{ flex: 1, minWidth: 0 }}>
                    {row.operator_name || 'Неизвестно'}
                  </Text>
                  <Text size="xs" c="dimmed" style={{ flexShrink: 0 }}>
                    {formatTimelineMetricDuration(row.total_seconds)}
                  </Text>
                </Group>
              ))}
            </Stack>
          </Box>
        ) : null}
      </Stack>
    </Paper>
  )
}
